import { usEnglish } from "./us-english";
import contractData from "../data/participant-app-contract.json";

export type ProductLocale = "en" | "es";

export type LocalizedText = {
  en: string;
  es: string;
};

export type ContractText = Partial<LocalizedText> & {
  text?: string;
  locale?: string;
};

export type ContractEntryField = {
  id: string;
  label: ContractText;
  help: ContractText | null;
  answer: {
    shape: string;
    cardinality: "one" | "many";
    options: Array<{ value: string; label: ContractText }> | null;
    vocabulary: unknown;
  };
  requiredness: "required" | "conditionally_required" | "optional";
};

export type ContractConcept = {
  id: string;
  answer_key: string;
  section: string | null;
  label: ContractText | null;
  prompt: ContractText | null;
  help: ContractText | null;
  requiredness: "required" | "conditionally_required" | "optional";
  applicability?: {
    human: ContractText | null;
    machine: string | null;
    machine_status: string;
  };
  answer: {
    shape: string;
    cardinality: "one" | "many";
    options: Array<{ value: string; label: ContractText }> | null;
    vocabulary: unknown;
    entry_shape: ContractEntryField[] | null;
  };
};

export type DocumentRequest = {
  id: string;
  title: LocalizedText;
  description: LocalizedText;
  example: LocalizedText | null;
  requiredness: "required" | "conditionally_required" | "optional";
  applicability?: {
    human: LocalizedText | null;
    machine: string | null;
    machine_status: string;
  };
};

type CountryContract = {
  code: "US" | "ES" | "IT";
  name: LocalizedText;
  concepts: ContractConcept[];
  document_requests: DocumentRequest[];
};

type ParticipantContract = {
  locales: { product_locales: ProductLocale[] };
  schema: "gm.participant_app_contract";
  schema_version: string;
  status: "proposed_noncanonical";
  content_hierarchy: Array<{
    key: string;
    title: LocalizedText;
    purpose: LocalizedText;
    collects_answers: boolean;
  }>;
  concepts: ContractConcept[];
  countries: Record<"US" | "ES" | "IT", CountryContract>;
};

export const participantContract = contractData as ParticipantContract;

export function localized(value: ContractText | null | undefined, locale: ProductLocale) {
  const text = value?.[locale] ?? value?.en ?? value?.text ?? "";
  return locale === "en" ? usEnglish(text) : text;
}

export function section(key: string) {
  return participantContract.content_hierarchy.find((item) => item.key === key);
}

export function concept(id: string) {
  return participantContract.concepts.find((item) => item.id === id);
}

export function country(code: "US" | "ES" | "IT") {
  return participantContract.countries[code];
}
